import { useCallback, useEffect, useRef, useState } from 'react';
import { podeFazer } from '../../acesso';
import type { Eu } from '../../acesso';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import {
  confirmarChegada,
  conferir,
  conferirTudo,
  dataHoraBr,
  procurarNota,
} from '../../encomendas-fluxo';
import type { ItemLigado } from '../../encomendas-fluxo';
import {
  FILTROS_VAZIOS,
  ROTULO_DA_SITUACAO,
  abrirNota,
  atualizarNotas,
  diaBr,
  filtrosLigados,
  haQuantoTempo,
  linhaDoTempoDoProduto,
  listarNotas,
  vencimento,
} from '../../notas-central';
import type {
  AbaDasNotas,
  Contagem,
  FiltrosDasNotas,
  ItemDaNota,
  LinhaDoTempoDoProduto,
  ListaDeNotas,
  NotaCompleta,
  NotaDaLista,
} from '../../notas-central';
import { Leitor } from '../balconista/Leitor';
import { Gaveta } from './Gaveta';
import './notas.css';

/**
 * RECEBIMENTO & NOTAS (fase 7 → central de notas, dono 14/10/2026).
 *
 * Em cima, o campo de sempre: bipar a CHAVE da NF abre a nota; número ou fornecedor filtram a
 * lista. Embaixo, TODAS as notas de compra em quatro abas — A caminho · Chegou, falta entrada ·
 * Deu entrada · Todas — com filtros (fornecedor, produto, período, bonificação, encomenda, valor).
 * A nota aberta mostra a LINHA DO TEMPO (emitida → SEFAZ → bipou → entrada no HOS → conferida),
 * todos os itens, os boletos e as encomendas ligadas; cada produto abre a linha do tempo dele.
 *
 * Bipou e entrada são etapas separadas, cada uma com hora e quem (dono: "precisamos saber se a
 * nota chegou (bipou) e quando deu entrada"). Valor (total, custo, boletos) só para quem tem
 * `notas.ver_valores` — o servidor nem manda para os outros.
 */

type Resultado = 'CORRETO' | 'VEIO_MENOS' | 'VEIO_MAIS' | 'NAO_VEIO' | 'AVARIA' | 'OUTRO';

const BRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const reais = (v: number | null | undefined) =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : BRL.format(v);
const qtd = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { maximumFractionDigits: 3 });

const CHAVE_DOS_FILTROS = 'inovapay.recebimento.filtros.v1';
function filtrosGuardados(): FiltrosDasNotas {
  try {
    const t = window.localStorage.getItem(CHAVE_DOS_FILTROS);
    return t
      ? { ...FILTROS_VAZIOS, ...(JSON.parse(t) as Partial<FiltrosDasNotas>) }
      : FILTROS_VAZIOS;
  } catch {
    return FILTROS_VAZIOS;
  }
}
function guardarFiltros(f: FiltrosDasNotas) {
  try {
    window.localStorage.setItem(CHAVE_DOS_FILTROS, JSON.stringify(f));
  } catch {
    // navegador sem armazenamento: só não lembra
  }
}

const ABAS: readonly { aba: AbaDasNotas; rotulo: string; chave: keyof Contagem | null }[] = [
  { aba: 'A_CAMINHO', rotulo: 'A caminho', chave: 'A_CAMINHO' },
  { aba: 'NA_LOJA', rotulo: 'Chegou · falta entrada', chave: 'NA_LOJA' },
  { aba: 'ENTRADA', rotulo: 'Deu entrada', chave: 'ENTRADA' },
  { aba: 'TODAS', rotulo: 'Todas', chave: null },
];

export function Recebimento({ sessao, eu }: { sessao: Sessao; eu: Eu | null }) {
  const podeConferir = podeFazer(eu, 'encomendas.conferir');
  const [filtros, setFiltrosCrus] = useState<FiltrosDasNotas>(filtrosGuardados);
  const [painelAberto, setPainelAberto] = useState(false);
  const [termo, setTermo] = useState('');
  const [camera, setCamera] = useState(false);
  const [lista, setLista] = useState<ListaDeNotas | null>(null);
  const [mais, setMais] = useState<readonly NotaDaLista[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aberta, setAberta] = useState<string | null>(null);
  const [atualizando, setAtualizando] = useState(false);
  const [produto, setProduto] = useState<{
    codigo: string;
    ean: string | null;
    nome: string | null;
  } | null>(null);
  const entrada = useRef<HTMLInputElement>(null);
  const pedido = useRef(0);

  const setFiltros = (f: FiltrosDasNotas) => {
    setFiltrosCrus(f);
    guardarFiltros(f);
  };

  const carregar = useCallback(
    async (silencioso = false) => {
      const meu = ++pedido.current;
      if (!silencioso) setCarregando(true);
      const r = await listarNotas(filtros);
      if (meu !== pedido.current) return;
      setCarregando(false);
      if (!r.ok) {
        setErro(
          r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não foi possível carregar as notas.',
        );
        return;
      }
      setErro(null);
      setLista(r.dados);
      setMais([]);
    },
    [filtros],
  );

  useEffect(() => {
    // digitação nos filtros de texto: espera parar de digitar
    const t = setTimeout(() => void carregar(), 250);
    return () => clearTimeout(t);
  }, [carregar]);
  useEffect(() => {
    if (aberta) return;
    const t = setInterval(() => void carregar(true), 60_000);
    return () => clearInterval(t);
  }, [carregar, aberta]);
  useEffect(() => {
    entrada.current?.focus();
  }, []);

  function buscar(t: string) {
    const limpo = t.replace(/\s/g, '');
    if (/^\d{44}$/.test(limpo)) {
      setTermo('');
      setAberta(limpo);
      return;
    }
    setFiltros({ ...filtros, q: t.trim(), aba: t.trim() ? 'TODAS' : filtros.aba });
  }

  async function puxarAgora() {
    setAtualizando(true);
    await atualizarNotas(sessao);
    setAtualizando(false);
    await carregar(true);
  }

  async function carregarMais() {
    if (!lista) return;
    const r = await listarNotas(filtros, lista.notas.length + mais.length);
    if (r.ok) setMais([...mais, ...r.dados.notas]);
  }

  if (aberta)
    return (
      <div className="nt">
        <NotaAberta
          chave={aberta}
          sessao={sessao}
          podeConferir={podeConferir}
          aoVoltar={() => {
            setAberta(null);
            void carregar(true);
          }}
          aoAbrirProduto={setProduto}
        />
        {produto && (
          <GavetaDoProduto
            {...produto}
            aoFechar={() => setProduto(null)}
            aoAbrirNota={(c) => {
              setProduto(null);
              setAberta(c);
            }}
          />
        )}
      </div>
    );

  const notas = [...(lista?.notas ?? []), ...mais];
  const contagem = lista?.contagem ?? null;
  const ligados = filtrosLigados(filtros);

  return (
    <div className="nt">
      <section className="nt-topo">
        <div className="nt-topo-titulo">
          <h1>Recebimento e notas</h1>
          <p>Bipe a chave da NF para abrir a nota. Número ou fornecedor procuram na lista.</p>
        </div>
        <div className="enc-busca-campo nt-bipar">
          <Icone nome="scan" tamanho={20} traco={1.7} />
          <input
            ref={entrada}
            value={termo}
            onChange={(e) => {
              setTermo(e.target.value);
              if (/^\d{44}$/.test(e.target.value.replace(/\s/g, ''))) buscar(e.target.value);
            }}
            onKeyDown={(e) => e.key === 'Enter' && buscar(termo)}
            placeholder="Chave de 44 dígitos, número da NF ou fornecedor"
            aria-label="Chave da NF, número ou fornecedor"
            inputMode="search"
            autoComplete="off"
          />
          {filtros.q && (
            <button
              type="button"
              className="enc-busca-limpar"
              onClick={() => {
                setTermo('');
                setFiltros({ ...filtros, q: '' });
              }}
            >
              Limpar
            </button>
          )}
          <button type="button" className="enc-busca-camera" onClick={() => setCamera(true)}>
            <Icone nome="cam" tamanho={18} traco={1.7} />
            <span>Câmera</span>
          </button>
        </div>
      </section>

      <Resumo
        contagem={contagem}
        aoEscolher={(f) => setFiltros({ ...FILTROS_VAZIOS, ...f })}
        filtros={filtros}
      />

      <section className="nt-lista">
        <header className="nt-lista-cabeca">
          <div className="nt-abas" role="tablist" aria-label="Situação da nota">
            {ABAS.map((a) => (
              <button
                key={a.aba}
                type="button"
                role="tab"
                aria-selected={filtros.aba === a.aba}
                className={filtros.aba === a.aba ? 'nt-aba-sel' : ''}
                onClick={() => setFiltros({ ...filtros, aba: a.aba })}
              >
                {a.rotulo}
                {contagem && a.chave && (
                  <span className="nt-aba-n">{String(contagem[a.chave])}</span>
                )}
              </button>
            ))}
          </div>
          <div className="nt-lista-acoes">
            <button
              type="button"
              className={`enc-botao-sec${ligados ? ' nt-filtro-ligado' : ''}`}
              aria-expanded={painelAberto}
              onClick={() => setPainelAberto(!painelAberto)}
            >
              <Icone nome="filter" tamanho={15} />
              Filtros{ligados ? <span className="enc-contador">{String(ligados)}</span> : null}
            </button>
            <button
              type="button"
              className="enc-botao-sec"
              onClick={() => void puxarAgora()}
              disabled={atualizando}
              title="Puxar agora da SEFAZ (InovaPED) e do HOS"
            >
              <Icone nome="retry" tamanho={15} />
              {atualizando ? 'Atualizando…' : 'Atualizar'}
            </button>
          </div>
        </header>

        {painelAberto && (
          <PainelDeFiltros
            filtros={filtros}
            veValores={lista?.veValores ?? false}
            aoMudar={setFiltros}
            aoLimpar={() => {
              setTermo('');
              setFiltros({ ...FILTROS_VAZIOS, aba: filtros.aba });
            }}
          />
        )}
        {!painelAberto && ligados > 0 && (
          <FiltrosAtivos
            filtros={filtros}
            aoMudar={setFiltros}
            aoLimpar={() => {
              setTermo('');
              setFiltros({ ...FILTROS_VAZIOS, aba: filtros.aba });
            }}
          />
        )}
        {lista?.avisoDoProduto && (
          <p className="enc-faixa-aviso">
            A busca por produto não respondeu ({lista.avisoDoProduto}). A lista mostra sem esse
            filtro.
          </p>
        )}

        {carregando && !lista ? (
          <div className="nt-esqueleto" aria-busy="true">
            <i />
            <i />
            <i />
          </div>
        ) : erro ? (
          <p className="enc-erro">{erro}</p>
        ) : notas.length === 0 ? (
          <div className="enc-nada">
            <b>
              {filtros.aba === 'A_CAMINHO' && !ligados
                ? 'Nenhuma nota a caminho.'
                : 'Nenhuma nota com esses filtros.'}
            </b>
            <span>
              {ligados
                ? 'Tire algum filtro ou troque a aba.'
                : 'Quando a SEFAZ entregar uma nota, ela aparece aqui.'}
            </span>
          </div>
        ) : (
          <>
            <ul className="nt-cartoes">
              {notas.map((n) => (
                <li key={n.chave}>
                  <CartaoDaNota nota={n} aoAbrir={() => setAberta(n.chave)} />
                </li>
              ))}
            </ul>
            <footer className="nt-lista-pe">
              <span>
                {String(notas.length)} de {String(lista?.total ?? notas.length)} nota
                {(lista?.total ?? 0) === 1 ? '' : 's'}
                {lista?.atualizadoEm ? ` · atualizado ${dataHoraBr(lista.atualizadoEm)}` : ''}
              </span>
              {lista && notas.length < lista.total && (
                <button type="button" className="enc-botao-sec" onClick={() => void carregarMais()}>
                  Carregar mais
                </button>
              )}
            </footer>
          </>
        )}
      </section>

      {camera && (
        <Leitor
          aoLer={(c) => {
            setCamera(false);
            setTermo(c);
            buscar(c);
          }}
          aoFechar={() => setCamera(false)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Resumo (os números que viram filtro)
 * ------------------------------------------------------------------ */

function Resumo({
  contagem,
  filtros,
  aoEscolher,
}: {
  contagem: Contagem | null;
  filtros: FiltrosDasNotas;
  aoEscolher: (f: Partial<FiltrosDasNotas>) => void;
}) {
  const cartoes: {
    rotulo: string;
    n: number | null;
    dica: string;
    icone: NomeDoIcone;
    tom: string;
    filtro: Partial<FiltrosDasNotas>;
    sel: boolean;
  }[] = [
    {
      rotulo: 'A caminho',
      n: contagem?.A_CAMINHO ?? null,
      dica: contagem?.atrasadas
        ? `${String(contagem.atrasadas)} há mais de 5 dias`
        : 'emitidas, ainda não chegaram',
      icone: 'clock',
      tom: contagem?.atrasadas ? 'atencao' : 'neutro',
      filtro: { aba: 'A_CAMINHO' },
      sel: filtros.aba === 'A_CAMINHO' && !filtros.bonificacao && !filtros.semBipe,
    },
    {
      rotulo: 'Chegou · falta entrada',
      n: contagem?.NA_LOJA ?? null,
      dica: 'na loja, sem entrada no HOS',
      icone: 'box',
      tom: contagem?.NA_LOJA ? 'info' : 'neutro',
      filtro: { aba: 'NA_LOJA' },
      sel: filtros.aba === 'NA_LOJA',
    },
    {
      rotulo: 'Deu entrada',
      n: contagem?.ENTRADA ?? null,
      dica: 'efetivadas no HOS',
      icone: 'check',
      tom: 'ok',
      filtro: { aba: 'ENTRADA' },
      sel: filtros.aba === 'ENTRADA' && !filtros.semBipe,
    },
    {
      rotulo: 'Entrada sem bipe',
      n: contagem?.semBipe ?? null,
      dica: 'entraram no HOS e ninguém bipou',
      icone: 'warn',
      tom: contagem?.semBipe ? 'atencao' : 'neutro',
      filtro: { aba: 'ENTRADA', semBipe: true },
      sel: filtros.semBipe,
    },
    {
      rotulo: 'Com bonificação',
      n: contagem?.bonificacao ?? null,
      dica: 'itens que vieram de brinde',
      icone: 'trophy',
      tom: contagem?.bonificacao ? 'bonus' : 'neutro',
      filtro: { aba: 'TODAS', bonificacao: true },
      sel: filtros.bonificacao,
    },
  ];
  return (
    <div className="nt-resumo">
      {cartoes.map((c) => (
        <button
          key={c.rotulo}
          type="button"
          className={`nt-kpi nt-kpi-${c.tom}${c.sel ? ' nt-kpi-sel' : ''}`}
          onClick={() => aoEscolher(c.filtro)}
          aria-pressed={c.sel}
        >
          <span className="nt-kpi-topo">
            <Icone nome={c.icone} tamanho={15} />
            {c.rotulo}
          </span>
          <b>{c.n === null ? '…' : String(c.n)}</b>
          <small>{c.dica}</small>
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Filtros
 * ------------------------------------------------------------------ */

function PainelDeFiltros({
  filtros,
  veValores,
  aoMudar,
  aoLimpar,
}: {
  filtros: FiltrosDasNotas;
  veValores: boolean;
  aoMudar: (f: FiltrosDasNotas) => void;
  aoLimpar: () => void;
}) {
  const campo = (k: keyof FiltrosDasNotas, rotulo: string, extra: Record<string, string> = {}) => (
    <label className="enc-campo">
      <span className="enc-campo-rotulo">{rotulo}</span>
      <input
        value={String(filtros[k])}
        onChange={(e) => aoMudar({ ...filtros, [k]: e.target.value })}
        {...extra}
      />
    </label>
  );
  const alternar = (k: 'bonificacao' | 'comEncomenda' | 'semBipe', rotulo: string) => (
    <button
      type="button"
      className={filtros[k] ? 'enc-filtro-sel' : ''}
      aria-pressed={filtros[k]}
      onClick={() => aoMudar({ ...filtros, [k]: !filtros[k] })}
    >
      {rotulo}
    </button>
  );
  const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const diasAtras = (n: number) =>
    new Date(Date.now() - n * 86_400_000).toLocaleDateString('sv-SE', {
      timeZone: 'America/Sao_Paulo',
    });
  return (
    <div className="nt-filtros">
      <div className="nt-filtros-grade">
        {campo('q', 'Número, fornecedor ou chave', { placeholder: 'Ex.: 563539 ou Profarma' })}
        {campo('fornecedor', 'Fornecedor', { placeholder: 'Nome do fornecedor' })}
        {campo('produto', 'Produto na nota', { placeholder: 'Nome, EAN ou código do HOS' })}
        {campo('de', 'Emissão de', { type: 'date' })}
        {campo('ate', 'Emissão até', { type: 'date' })}
        {veValores &&
          campo('valorMin', 'Valor mínimo (R$)', { inputMode: 'decimal', placeholder: '0,00' })}
        {veValores &&
          campo('valorMax', 'Valor máximo (R$)', { inputMode: 'decimal', placeholder: '0,00' })}
      </div>
      <div className="nt-filtros-linha">
        <div className="enc-filtros">
          <button type="button" onClick={() => aoMudar({ ...filtros, de: hoje, ate: hoje })}>
            Hoje
          </button>
          <button
            type="button"
            onClick={() => aoMudar({ ...filtros, de: diasAtras(6), ate: hoje })}
          >
            7 dias
          </button>
          <button
            type="button"
            onClick={() => aoMudar({ ...filtros, de: diasAtras(29), ate: hoje })}
          >
            30 dias
          </button>
        </div>
        <div className="enc-filtros">
          {alternar('bonificacao', 'Com bonificação')}
          {alternar('comEncomenda', 'Com encomenda')}
          {alternar('semBipe', 'Sem bipe')}
        </div>
        <button type="button" className="enc-link" onClick={aoLimpar}>
          Limpar filtros
        </button>
      </div>
    </div>
  );
}

function FiltrosAtivos({
  filtros,
  aoMudar,
  aoLimpar,
}: {
  filtros: FiltrosDasNotas;
  aoMudar: (f: FiltrosDasNotas) => void;
  aoLimpar: () => void;
}) {
  const chips: { rotulo: string; tirar: Partial<FiltrosDasNotas> }[] = [];
  if (filtros.q) chips.push({ rotulo: `“${filtros.q}”`, tirar: { q: '' } });
  if (filtros.fornecedor)
    chips.push({ rotulo: `Fornecedor: ${filtros.fornecedor}`, tirar: { fornecedor: '' } });
  if (filtros.produto)
    chips.push({ rotulo: `Produto: ${filtros.produto}`, tirar: { produto: '' } });
  if (filtros.de || filtros.ate)
    chips.push({
      rotulo: `Emissão ${filtros.de ? diaBr(filtros.de) : '…'} a ${filtros.ate ? diaBr(filtros.ate) : '…'}`,
      tirar: { de: '', ate: '' },
    });
  if (filtros.bonificacao) chips.push({ rotulo: 'Com bonificação', tirar: { bonificacao: false } });
  if (filtros.comEncomenda) chips.push({ rotulo: 'Com encomenda', tirar: { comEncomenda: false } });
  if (filtros.semBipe) chips.push({ rotulo: 'Sem bipe', tirar: { semBipe: false } });
  if (filtros.valorMin || filtros.valorMax)
    chips.push({
      rotulo: `Valor ${filtros.valorMin || '0'} a ${filtros.valorMax || '…'}`,
      tirar: { valorMin: '', valorMax: '' },
    });
  return (
    <div className="nt-ativos">
      {chips.map((c) => (
        <button
          key={c.rotulo}
          type="button"
          className="nt-chip"
          onClick={() => aoMudar({ ...filtros, ...c.tirar })}
          aria-label={`Tirar filtro ${c.rotulo}`}
        >
          {c.rotulo}
          <Icone nome="x" tamanho={12} traco={2} />
        </button>
      ))}
      <button type="button" className="enc-link" onClick={aoLimpar}>
        Limpar
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Cartão da nota (lista)
 * ------------------------------------------------------------------ */

const TOM_DA_SITUACAO: Readonly<Record<NotaDaLista['situacao'], string>> = {
  A_CAMINHO: 'nt-sit-caminho',
  NA_LOJA: 'nt-sit-loja',
  ENTRADA: 'nt-sit-entrada',
  CANCELADA: 'nt-sit-cancelada',
};

function Etapas({ n }: { n: NotaDaLista }) {
  const pulouBipe = !n.bipadaEm && !!n.hosEntradaEm;
  const passos = [
    { rotulo: 'Emitida', em: n.emissao, feito: !!n.emissao, quem: null, pulado: false },
    { rotulo: 'Bipou', em: n.bipadaEm, feito: !!n.bipadaEm, quem: n.bipadaPor, pulado: pulouBipe },
    {
      rotulo: 'Entrada HOS',
      em: n.hosEntradaEm,
      feito: !!n.hosEntradaEm,
      quem: n.hosOperador,
      pulado: false,
    },
  ];
  return (
    <ol className="nt-etapas" aria-label="Etapas da nota">
      {passos.map((p) => (
        <li
          key={p.rotulo}
          className={p.feito ? 'nt-etapa-feita' : p.pulado ? 'nt-etapa-pulada' : ''}
        >
          <i aria-hidden="true">
            {p.feito && <Icone nome="check" tamanho={10} traco={3} />}
            {p.pulado && '!'}
          </i>
          <span>
            <b>{p.rotulo}</b>
            <small>
              {p.feito ? dataHoraBr(p.em) : p.pulado ? 'não bipada' : 'aguardando'}
              {p.quem ? ` · ${p.quem}` : ''}
            </small>
          </span>
        </li>
      ))}
    </ol>
  );
}

function CartaoDaNota({ nota: n, aoAbrir }: { nota: NotaDaLista; aoAbrir: () => void }) {
  const atrasada = n.situacao === 'A_CAMINHO' && (n.diasDesdeEmissao ?? 0) > 5;
  return (
    <button
      type="button"
      className={`nt-cartao${atrasada ? ' nt-cartao-atrasada' : ''}`}
      onClick={aoAbrir}
    >
      <div className="nt-cartao-topo">
        <div className="nt-cartao-id">
          <b>NF {n.numero ?? '—'}</b>
          <span>{n.fornecedor ?? 'Fornecedor —'}</span>
        </div>
        <span className={`nt-sit ${TOM_DA_SITUACAO[n.situacao]}`}>
          {ROTULO_DA_SITUACAO[n.situacao]}
        </span>
      </div>
      <div className="nt-cartao-meta">
        <span>
          Emissão {diaBr(n.emissao)}
          {n.situacao === 'A_CAMINHO' && n.diasDesdeEmissao !== null
            ? ` · ${haQuantoTempo(n.diasDesdeEmissao)}`
            : ''}
        </span>
        {n.itens !== null && n.itens > 0 && (
          <span>
            {String(n.itens)} {n.itens === 1 ? 'item' : 'itens'}
          </span>
        )}
        {n.valorTotalDaNota !== null && (
          <span className="nt-valor">{reais(n.valorTotalDaNota)}</span>
        )}
        {n.pedido && <span>Pedido {n.pedido}</span>}
      </div>
      <Etapas n={n} />
      {(n.bonificados > 0 ||
        n.itensDeEncomenda > 0 ||
        atrasada ||
        (n.hosEntradaEm && !n.bipadaEm)) && (
        <div className="nt-cartao-selos">
          {n.bonificados > 0 && (
            <span className="nt-selo nt-selo-bonus" title={n.bonificadosResumo ?? undefined}>
              🎁 {String(n.bonificados)} bonificado{n.bonificados === 1 ? '' : 's'}
            </span>
          )}
          {n.itensDeEncomenda > 0 && (
            <span className="enc-tag enc-tag-info">{String(n.itensDeEncomenda)} de encomenda</span>
          )}
          {atrasada && <span className="enc-tag enc-tag-atencao">Atrasada</span>}
          {n.hosEntradaEm && !n.bipadaEm && !n.cancelada && (
            <span className="enc-tag enc-tag-atencao">Entrada sem bipe</span>
          )}
        </div>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ *
 * A nota aberta
 * ------------------------------------------------------------------ */

type AbaDaNota = 'itens' | 'boletos' | 'encomendas';

function NotaAberta({
  chave,
  sessao,
  podeConferir,
  aoVoltar,
  aoAbrirProduto,
}: {
  chave: string;
  sessao: Sessao;
  podeConferir: boolean;
  aoVoltar: () => void;
  aoAbrirProduto: (p: { codigo: string; ean: string | null; nome: string | null }) => void;
}) {
  const [dados, setDados] = useState<NotaCompleta | null>(null);
  const [recebimento, setRecebimento] = useState<{
    id: string;
    recebidoEm: string;
    recebidoPor: string;
  } | null>(null);
  const [ligados, setLigados] = useState<readonly ItemLigado[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [aba, setAba] = useState<AbaDaNota>('itens');
  const [soBonificados, setSoBonificados] = useState(false);
  const [copiada, setCopiada] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const carregar = useCallback(async () => {
    // a nota completa (central) e o recebimento das encomendas (que já casa as linhas na hora)
    const [a, b] = await Promise.all([abrirNota(chave), procurarNota({ chave })]);
    if (!a.ok) {
      setErro(a.falha.tipo === 'invalido' ? a.falha.mensagem : 'Não foi possível abrir a nota.');
      return;
    }
    setErro(null);
    setDados(a.dados);
    if (b.ok && b.dados.fonte.ok) {
      setRecebimento(b.dados.recebimento ?? null);
      setLigados(b.dados.ligados ?? a.dados.ligados);
    } else setLigados(a.dados.ligados);
  }, [chave]);

  useEffect(() => {
    void carregar();
    window.scrollTo({ top: 0 });
  }, [carregar]);

  async function chegou() {
    setConfirmando(true);
    const r = await confirmarChegada(sessao, chave, null);
    setConfirmando(false);
    if (!r.ok)
      return setAviso(
        r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não foi possível confirmar a chegada.',
      );
    setAviso(
      r.dados.jaEstava
        ? 'Esta nota já tinha a chegada confirmada.'
        : `Chegada confirmada.${r.dados.itens ? ` ${String(r.dados.itens)} ${r.dados.itens === 1 ? 'item de encomenda avisado' : 'itens de encomenda avisados'}.` : ''}`,
    );
    await carregar();
  }

  if (erro)
    return (
      <>
        <Voltar aoVoltar={aoVoltar} />
        <p className="enc-erro">{erro}</p>
      </>
    );
  if (!dados)
    return (
      <>
        <Voltar aoVoltar={aoVoltar} />
        <div className="nt-esqueleto" aria-busy="true">
          <i />
          <i />
          <i />
        </div>
      </>
    );

  const n = dados.nota;
  const bipada = !!(recebimento ?? n.bipadaEm);
  const itensVisiveis = soBonificados ? dados.itens.filter((i) => i.bonificacao) : dados.itens;
  const bonificados = dados.itens.filter((i) => i.bonificacao);
  const abas: { aba: AbaDaNota; rotulo: string; n: number | null }[] = [
    { aba: 'itens', rotulo: 'Itens', n: dados.itens.length },
    ...(dados.veValores
      ? [{ aba: 'boletos' as const, rotulo: 'Boletos', n: dados.boletos?.parcelas.length ?? 0 }]
      : []),
    { aba: 'encomendas', rotulo: 'Encomendas', n: ligados.length },
  ];

  return (
    <>
      <Voltar aoVoltar={aoVoltar} />
      <header className="nt-nota-cabeca">
        <div className="nt-nota-id">
          <div className="nt-nota-titulo">
            <h1>NF {n.numero ?? '—'}</h1>
            <span className={`nt-sit ${TOM_DA_SITUACAO[n.situacao]}`}>
              {ROTULO_DA_SITUACAO[n.situacao]}
            </span>
          </div>
          <p className="nt-nota-forn">{n.fornecedor ?? '—'}</p>
          <dl className="nt-nota-dados">
            {n.cnpj && (
              <div>
                <dt>CNPJ</dt>
                <dd className="enc-mono">{n.cnpj}</dd>
              </div>
            )}
            <div>
              <dt>Emissão</dt>
              <dd>{diaBr(n.emissao)}</dd>
            </div>
            {n.pedido && (
              <div>
                <dt>Pedido</dt>
                <dd>{n.pedido}</dd>
              </div>
            )}
            <div>
              <dt>Itens</dt>
              <dd>{String(dados.itens.length || n.itens || 0)}</dd>
            </div>
            {dados.veValores && (
              <div>
                <dt>Total da nota</dt>
                <dd className="nt-valor">{reais(n.valorTotalDaNota)}</dd>
              </div>
            )}
          </dl>
          <button
            type="button"
            className="nt-chave"
            onClick={() => {
              void navigator.clipboard?.writeText(n.chave).then(() => {
                setCopiada(true);
                setTimeout(() => setCopiada(false), 1500);
              });
            }}
            title="Copiar a chave"
          >
            <span className="enc-mono">{n.chave.replace(/(\d{4})(?=\d)/g, '$1 ')}</span>
            <small>{copiada ? 'copiada' : 'copiar'}</small>
          </button>
        </div>
        <div className="nt-nota-acao">
          {bipada ? (
            <div className="enc-recebida">
              <Icone nome="check" tamanho={16} traco={2.2} /> Bipada{' '}
              {dataHoraBr(recebimento?.recebidoEm ?? n.bipadaEm)} ·{' '}
              {recebimento?.recebidoPor ?? n.bipadaPor ?? '—'}
            </div>
          ) : (
            <button
              type="button"
              className="enc-botao enc-botao-grande"
              onClick={() => void chegou()}
              disabled={n.cancelada || confirmando}
            >
              <Icone nome="scan" tamanho={18} traco={1.8} />
              {confirmando ? 'Confirmando…' : 'Confirmar chegada (bipou)'}
            </button>
          )}
          {n.cancelada && <span className="enc-selo-alerta">CANCELADA NA SEFAZ</span>}
        </div>
      </header>
      {aviso && (
        <p className="enc-faixa-ok" role="status">
          {aviso}
        </p>
      )}
      {bonificados.length > 0 && (
        <p className="nt-faixa-bonus">
          🎁{' '}
          <b>
            {String(bonificados.length)}{' '}
            {bonificados.length === 1 ? 'item bonificado' : 'itens bonificados'}
          </b>{' '}
          nesta nota — confira o brinde junto com a mercadoria.{' '}
          <button
            type="button"
            className="enc-link"
            onClick={() => {
              setAba('itens');
              setSoBonificados(true);
            }}
          >
            Ver só os bonificados
          </button>
        </p>
      )}
      {(!dados.fontes.inovaped.ok || !dados.fontes.hos.ok) && (
        <p className="enc-faixa-aviso">
          {!dados.fontes.inovaped.ok &&
            `InovaPED: ${dados.fontes.inovaped.mensagem ?? 'sem resposta'}. `}
          {!dados.fontes.hos.ok && `HOS: ${dados.fontes.hos.mensagem ?? 'sem resposta'}.`}
        </p>
      )}

      <div className="nt-nota-corpo">
        <section className="enc-col nt-tempo">
          <header className="enc-col-cabeca">
            <h2>Linha do tempo</h2>
          </header>
          <div className="enc-col-corpo">
            <ol className="enc-timeline">
              {dados.linhaDoTempo.map((p, k) => {
                const depoisFeito = dados.linhaDoTempo.slice(k + 1).some((x) => x.feito);
                const pulado = !p.feito && depoisFeito;
                const proximo =
                  !p.feito && !depoisFeito && dados.linhaDoTempo.slice(0, k).every((x) => x.feito);
                return (
                  <li
                    key={p.etapa}
                    className={
                      p.feito
                        ? 'enc-tl-feito'
                        : pulado
                          ? 'nt-tl-pulado'
                          : proximo
                            ? 'enc-tl-atual'
                            : ''
                    }
                  >
                    <i>
                      {p.feito && <Icone nome="check" tamanho={12} traco={3} />}
                      {pulado && '!'}
                    </i>
                    <div>
                      <b>{p.rotulo}</b>
                      <span>
                        {p.feito
                          ? dataHoraBr(p.em)
                          : pulado
                            ? 'não aconteceu'
                            : proximo
                              ? 'próximo passo'
                              : 'aguardando'}
                        {p.quem ? ` · ${p.quem}` : ''}
                      </span>
                      {p.detalhe && <span className="nt-tl-detalhe">{p.detalhe}</span>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </section>

        <section className="enc-col nt-conteudo">
          <header className="enc-col-cabeca nt-conteudo-cabeca">
            <div className="nt-abas nt-abas-pequenas" role="tablist">
              {abas.map((a) => (
                <button
                  key={a.aba}
                  type="button"
                  role="tab"
                  aria-selected={aba === a.aba}
                  className={aba === a.aba ? 'nt-aba-sel' : ''}
                  onClick={() => setAba(a.aba)}
                >
                  {a.rotulo}
                  {a.n !== null && <span className="nt-aba-n">{String(a.n)}</span>}
                </button>
              ))}
            </div>
            {aba === 'itens' && bonificados.length > 0 && (
              <label className="nt-so-bonus">
                <input
                  type="checkbox"
                  checked={soBonificados}
                  onChange={(e) => setSoBonificados(e.target.checked)}
                />
                Só bonificados
              </label>
            )}
          </header>
          <div className="enc-col-corpo">
            {aba === 'itens' &&
              (dados.itensDisponiveis === false ? (
                <p className="enc-dica">
                  A SEFAZ entregou só o resumo desta nota — os itens aparecem quando o XML completo
                  chegar ao InovaPED.
                </p>
              ) : (
                <TabelaDeItens
                  itens={itensVisiveis}
                  veValores={dados.veValores}
                  aoAbrirProduto={aoAbrirProduto}
                />
              ))}
            {aba === 'boletos' && <Boletos boletos={dados.boletos} />}
            {aba === 'encomendas' && (
              <ConferenciaDasEncomendas
                sessao={sessao}
                ligados={ligados}
                recebimento={recebimento}
                podeConferir={podeConferir}
                aoMudar={() => void carregar()}
              />
            )}
          </div>
        </section>
      </div>
    </>
  );
}

function Voltar({ aoVoltar }: { aoVoltar: () => void }) {
  return (
    <button type="button" className="nt-voltar" onClick={aoVoltar}>
      <Icone nome="arrow-l" tamanho={16} />
      Voltar às notas
    </button>
  );
}

function TabelaDeItens({
  itens,
  veValores,
  aoAbrirProduto,
}: {
  itens: readonly ItemDaNota[];
  veValores: boolean;
  aoAbrirProduto: (p: { codigo: string; ean: string | null; nome: string | null }) => void;
}) {
  if (!itens.length) return <span className="enc-col-vazio">Nenhum item.</span>;
  const total = veValores ? itens.reduce((s, i) => s + (i.custoTotal ?? 0), 0) : null;
  return (
    <div className="nt-itens">
      <table>
        <thead>
          <tr>
            <th>Produto</th>
            <th className="nt-num">Qtd</th>
            <th>Lote · validade</th>
            {veValores && <th className="nt-num">Custo un.</th>}
            {veValores && <th className="nt-num">Total</th>}
            <th className="nt-num">Conferido HOS</th>
            <th aria-label="Linha do tempo do produto" />
          </tr>
        </thead>
        <tbody>
          {itens.map((i, k) => (
            <tr
              key={`${i.linha ?? ''}-${String(k)}`}
              className={i.bonificacao ? 'nt-item-bonus' : i.deEncomenda ? 'nt-item-enc' : ''}
            >
              <td data-rotulo="Produto">
                {i.codigoHos ? (
                  <button
                    type="button"
                    className="nt-item-nome"
                    onClick={() =>
                      aoAbrirProduto({ codigo: i.codigoHos ?? '', ean: i.ean, nome: i.descricao })
                    }
                    title="Abrir a linha do tempo deste produto"
                  >
                    {i.descricao ?? '—'}
                  </button>
                ) : (
                  <b>{i.descricao ?? '—'}</b>
                )}
                <span className="nt-item-cod">
                  {i.codigoHos && <span className="enc-mono">Cód. {i.codigoHos}</span>}
                  {i.ean && <span className="enc-mono">EAN {i.ean}</span>}
                  {i.bonificacao && <span className="nt-selo nt-selo-bonus">🎁 Bonificação</span>}
                  {i.deEncomenda && <span className="enc-tag enc-tag-info">Encomenda</span>}
                </span>
              </td>
              <td data-rotulo="Qtd" className="nt-num">
                <span>
                  {qtd(i.quantidade)} <small>{i.unidade ?? ''}</small>
                </span>
              </td>
              <td data-rotulo="Lote · validade">
                {i.lote || i.validade ? (
                  <span>
                    {i.lote ?? '—'}
                    <small> · {i.validade ? diaBr(i.validade.slice(0, 10)) : '—'}</small>
                  </span>
                ) : (
                  '—'
                )}
              </td>
              {veValores && (
                <td data-rotulo="Custo un." className="nt-num">
                  {reais(i.custoUnitario)}
                </td>
              )}
              {veValores && (
                <td data-rotulo="Total" className="nt-num">
                  {i.bonificacao ? <span className="nt-brinde">brinde</span> : reais(i.custoTotal)}
                </td>
              )}
              <td data-rotulo="Conferido HOS" className="nt-num">
                {i.conferidoNoHos === null ? (
                  <span className="nt-apagado">—</span>
                ) : (
                  // compara com a quantidade que o PRÓPRIO HOS registrou na nota (mesma unidade)
                  <span
                    className={
                      i.quantidadeNoHos !== null && i.conferidoNoHos !== i.quantidadeNoHos
                        ? 'nt-diferente'
                        : 'nt-igual'
                    }
                    title={
                      i.quantidadeNoHos !== null
                        ? `Contou ${qtd(i.conferidoNoHos)} de ${qtd(i.quantidadeNoHos)} no HOS`
                        : undefined
                    }
                  >
                    {qtd(i.conferidoNoHos)}
                    {i.quantidadeNoHos !== null && i.conferidoNoHos !== i.quantidadeNoHos && (
                      <small> de {qtd(i.quantidadeNoHos)}</small>
                    )}
                  </span>
                )}
              </td>
              <td className="nt-acao">
                {i.codigoHos && (
                  <button
                    type="button"
                    className="nt-botao-icone"
                    onClick={() =>
                      aoAbrirProduto({ codigo: i.codigoHos ?? '', ean: i.ean, nome: i.descricao })
                    }
                    title="Linha do tempo do produto"
                    aria-label={`Linha do tempo de ${i.descricao ?? 'produto'}`}
                  >
                    <Icone nome="clock" tamanho={16} />
                    <span className="nt-so-celular">Linha do tempo</span>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        {total !== null && (
          <tfoot>
            <tr>
              <td colSpan={4}>Total dos itens</td>
              <td className="nt-num">{reais(total)}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function Boletos({ boletos }: { boletos: NotaCompleta['boletos'] }) {
  if (!boletos) return null;
  if (!boletos.ok)
    return (
      <p className="enc-faixa-aviso">
        Os boletos não puderam ser lidos agora{boletos.mensagem ? ` (${boletos.mensagem})` : ''}.
      </p>
    );
  if (!boletos.parcelas.length)
    return (
      <span className="enc-col-vazio">
        Esta nota não trouxe boletos no XML (à vista, ou a cobrança vem à parte).
      </span>
    );
  const total = boletos.parcelas.reduce((s, p) => s + (p.valorParcela ?? 0), 0);
  return (
    <div className="nt-boletos">
      {boletos.bloqueio && (
        <p className="enc-faixa-aviso">
          Pagamento TRAVADO no InovaPED{boletos.bloqueio.por ? ` por ${boletos.bloqueio.por}` : ''}
          {boletos.bloqueio.motivo ? `: ${boletos.bloqueio.motivo}` : ''}.
        </p>
      )}
      <ul>
        {boletos.parcelas.map((p) => {
          const v = vencimento(p.vence);
          return (
            <li key={p.parcela} className={p.pago ? 'nt-boleto-pago' : `nt-boleto-${v.tom}`}>
              <div>
                <b>Parcela {p.parcela}</b>
                <span>
                  Vence {diaBr(p.vence)} ·{' '}
                  {p.pago
                    ? `paga${p.pagoEm ? ` em ${diaBr(p.pagoEm.slice(0, 10))}` : ''}`
                    : v.texto}
                </span>
              </div>
              <b className="nt-valor">{reais(p.valorParcela)}</b>
            </li>
          );
        })}
      </ul>
      <p className="nt-boletos-total">
        <span>
          {String(boletos.parcelas.length)} parcela{boletos.parcelas.length === 1 ? '' : 's'}
        </span>
        <b className="nt-valor">{reais(total)}</b>
      </p>
      <p className="enc-dica">
        O XML da nota traz parcela, vencimento e valor. Linha digitável e código de barras vêm do
        banco do fornecedor.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Encomendas ligadas à nota (conferência item a item — fase 8)
 * ------------------------------------------------------------------ */

function ConferenciaDasEncomendas({
  sessao,
  ligados,
  recebimento,
  podeConferir,
  aoMudar,
}: {
  sessao: Sessao;
  ligados: readonly ItemLigado[];
  recebimento: { id: string; recebidoEm: string; recebidoPor: string } | null;
  podeConferir: boolean;
  aoMudar: () => void;
}) {
  const [contagem, setContagem] = useState<
    Record<string, { qtd: string; resultado: Resultado; obs: string }>
  >({});
  const [msg, setMsg] = useState<string | null>(null);
  const pendentes = ligados.filter((l) => l.conferido === null);

  if (!ligados.length)
    return (
      <span className="enc-col-vazio">
        Nenhum item desta nota está ligado a encomenda de cliente.
      </span>
    );

  async function conferirUm(l: ItemLigado) {
    if (!recebimento) return;
    const c = contagem[l.itemId] ?? {
      qtd: String(l.quantidade),
      resultado: 'CORRETO' as Resultado,
      obs: '',
    };
    const r = await conferir(sessao, recebimento.id, [
      {
        itemId: l.itemId,
        quantidadeRecebida: Number(c.qtd) || 0,
        resultado: c.resultado === 'CORRETO' ? null : c.resultado,
        obs: c.obs || null,
      },
    ]);
    if (!r.ok)
      return setMsg(r.falha.tipo === 'invalido' ? r.falha.mensagem : 'Não foi possível conferir.');
    setMsg(r.dados.divergencias ? 'Divergência registrada — o admin foi avisado.' : 'Conferido.');
    aoMudar();
  }
  async function tudo() {
    if (!recebimento) return;
    const r = await conferirTudo(sessao, recebimento.id);
    if (!r.ok) return setMsg('Não foi possível conferir.');
    setMsg(`${String(r.dados.conferidos)} conferido(s).`);
    aoMudar();
  }

  return (
    <div className="nt-encomendas">
      <p className="enc-faixa-destaque">
        <b>
          {String(ligados.length)}{' '}
          {ligados.length === 1
            ? 'item desta nota é de encomenda'
            : 'itens desta nota são de encomendas'}
        </b>
        {!recebimento
          ? ' — confirme a chegada (bipou) para conferir.'
          : pendentes.length
            ? ' — confira abaixo.'
            : ' — tudo conferido.'}
      </p>
      {recebimento && podeConferir && pendentes.length > 1 && (
        <button type="button" className="enc-botao-sec" onClick={() => void tudo()}>
          Conferir todos (quantidade certa)
        </button>
      )}
      {msg && <p className="enc-faixa-ok">{msg}</p>}
      {ligados.map((l) => {
        const c = contagem[l.itemId] ?? {
          qtd: String(l.quantidade),
          resultado: 'CORRETO' as Resultado,
          obs: '',
        };
        return (
          <div key={l.vinculoId} className="enc-conf">
            <div className="enc-conf-id">
              <b>{l.descricao}</b>
              <span>
                {l.codigo} · {l.cliente}
                {l.urgente ? ' · URGENTE' : ''} · esperado {String(l.quantidade)} un
              </span>
            </div>
            {l.conferido ? (
              <span
                className={`enc-tag ${l.conferido.resultado === 'CORRETO' ? 'enc-tag-ok' : 'enc-tag-problema'}`}
              >
                {l.conferido.resultado === 'CORRETO'
                  ? 'Conferido'
                  : l.conferido.resultado.replace('_', ' ').toLowerCase()}{' '}
                · {String(l.conferido.quantidade)} un
              </span>
            ) : recebimento && podeConferir ? (
              <div className="enc-conf-acoes">
                <input
                  aria-label="Quantidade recebida"
                  inputMode="numeric"
                  value={c.qtd}
                  onChange={(e) =>
                    setContagem({
                      ...contagem,
                      [l.itemId]: { ...c, qtd: e.target.value.replace(/\D/g, '') },
                    })
                  }
                />
                <select
                  aria-label="Resultado"
                  value={c.resultado}
                  onChange={(e) =>
                    setContagem({
                      ...contagem,
                      [l.itemId]: { ...c, resultado: e.target.value as Resultado },
                    })
                  }
                >
                  <option value="CORRETO">Correto</option>
                  <option value="VEIO_MENOS">Veio menos</option>
                  <option value="VEIO_MAIS">Veio mais</option>
                  <option value="NAO_VEIO">Não veio</option>
                  <option value="AVARIA">Avaria</option>
                  <option value="OUTRO">Outro</option>
                </select>
                {(c.resultado === 'AVARIA' || c.resultado === 'OUTRO') && (
                  <input
                    aria-label="O que aconteceu"
                    placeholder="O que aconteceu?"
                    value={c.obs}
                    onChange={(e) =>
                      setContagem({ ...contagem, [l.itemId]: { ...c, obs: e.target.value } })
                    }
                  />
                )}
                <button type="button" className="enc-botao" onClick={() => void conferirUm(l)}>
                  Conferir
                </button>
              </div>
            ) : (
              <span className="enc-tag">
                {recebimento ? 'Aguardando conferência' : 'Confirme a chegada'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * A linha do tempo do PRODUTO (gaveta)
 * ------------------------------------------------------------------ */

const ICONE_DO_EVENTO: Readonly<Record<string, NomeDoIcone>> = {
  PEDIDO: 'receipt',
  NOTA: 'file',
  EM_CONFERENCIA_HOS: 'scan',
  ENTRADA_HOS: 'box',
  ENCOMENDA: 'encomendar',
};
const ROTULO_DO_EVENTO: Readonly<Record<string, string>> = {
  PEDIDO: 'Pedido',
  NOTA: 'Nota fiscal',
  EM_CONFERENCIA_HOS: 'Em conferência',
  ENTRADA_HOS: 'Entrada no HOS',
  ENCOMENDA: 'Encomenda',
};

function GavetaDoProduto({
  codigo,
  ean,
  nome,
  aoFechar,
  aoAbrirNota,
}: {
  codigo: string;
  ean: string | null;
  nome: string | null;
  aoFechar: () => void;
  aoAbrirNota: (chave: string) => void;
}) {
  const [dados, setDados] = useState<LinhaDoTempoDoProduto | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tipo, setTipo] = useState<string>('TODOS');
  useEffect(() => {
    void linhaDoTempoDoProduto(codigo, ean).then((r) =>
      r.ok ? setDados(r.dados) : setErro('Não foi possível montar a linha do tempo.'),
    );
  }, [codigo, ean]);
  const eventos = (dados?.eventos ?? []).filter((e) => tipo === 'TODOS' || e.tipo === tipo);
  const tipos = [...new Set((dados?.eventos ?? []).map((e) => e.tipo))];
  return (
    <Gaveta
      titulo={nome ?? dados?.descricao ?? `Produto ${codigo}`}
      subtitulo={
        <span className="enc-mono">
          Cód. {codigo}
          {ean ? ` · EAN ${ean}` : ''}
        </span>
      }
      aoFechar={aoFechar}
      largura={560}
    >
      {erro && <p className="enc-erro">{erro}</p>}
      {!dados && !erro && (
        <div className="nt-esqueleto" aria-busy="true">
          <i />
          <i />
        </div>
      )}
      {dados && (
        <div className="nt-produto">
          {dados.aCaminho > 0 && (
            <p className="enc-faixa-destaque">
              <b>
                {String(dados.aCaminho)} nota{dados.aCaminho === 1 ? '' : 's'} a caminho
              </b>{' '}
              com este produto.
            </p>
          )}
          {(!dados.fontes.inovaped.ok || !dados.fontes.hos.ok) && (
            <p className="enc-faixa-aviso">
              {!dados.fontes.inovaped.ok &&
                `InovaPED: ${dados.fontes.inovaped.mensagem ?? 'sem resposta'}. `}
              {!dados.fontes.hos.ok && `HOS: ${dados.fontes.hos.mensagem ?? 'sem resposta'}.`}
            </p>
          )}
          {tipos.length > 1 && (
            <div className="enc-filtros">
              <button
                type="button"
                className={tipo === 'TODOS' ? 'enc-filtro-sel' : ''}
                onClick={() => setTipo('TODOS')}
              >
                Tudo
              </button>
              {tipos.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={tipo === t ? 'enc-filtro-sel' : ''}
                  onClick={() => setTipo(t)}
                >
                  {ROTULO_DO_EVENTO[t] ?? t}
                </button>
              ))}
            </div>
          )}
          {eventos.length === 0 ? (
            <span className="enc-col-vazio">Nada registrado para este produto.</span>
          ) : (
            <ol className="nt-eventos">
              {eventos.map((e, k) => (
                <li
                  key={`${e.tipo}-${String(k)}`}
                  className={`nt-evento nt-evento-${e.tipo.toLowerCase()}`}
                >
                  <i aria-hidden="true">
                    <Icone nome={ICONE_DO_EVENTO[e.tipo] ?? 'dots'} tamanho={14} />
                  </i>
                  <div>
                    <span className="nt-evento-quando">
                      {ROTULO_DO_EVENTO[e.tipo] ?? e.tipo} · {e.em ? dataHoraBr(e.em) : 'sem data'}
                    </span>
                    {e.chave ? (
                      <button
                        type="button"
                        className="nt-evento-titulo enc-link"
                        onClick={() => aoAbrirNota(e.chave ?? '')}
                      >
                        {e.titulo}
                      </button>
                    ) : (
                      <b className="nt-evento-titulo">{e.titulo}</b>
                    )}
                    <span className="nt-evento-detalhe">
                      {e.quantidade !== null && `${qtd(e.quantidade)} un`}
                      {e.custoUnitario !== null && ` · ${reais(e.custoUnitario)}/un`}
                      {e.detalhe && `${e.quantidade !== null ? ' · ' : ''}${e.detalhe}`}
                      {e.bonificacao && ' 🎁'}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </Gaveta>
  );
}
